/**
 * Allow only digits and a single decimal point (for currency-style cost fields).
 * Strips letters, symbols, and extra dots.
 */
export function sanitizeDecimalCostInput(raw) {
  if (raw == null || raw === "") return "";
  const s = String(raw).replace(/[^\d.]/g, "");
  const dot = s.indexOf(".");
  if (dot === -1) return s;
  return s.slice(0, dot + 1) + s.slice(dot + 1).replace(/\./g, "");
}

/**
 * True if at least one tier has a positive cost (direct tier amounts or stratification row sums).
 */
export function hasPositiveTierCosts(stratification, tiers) {
  if (!Array.isArray(tiers) || tiers.length === 0) return false;
  if (!stratification) {
    return tiers.some((t) => Number(String(t?.cost ?? "").trim() || 0) > 0);
  }
  return tiers.some((tier) => {
    const sum = (tier.rows || []).reduce(
      (s, r) => s + Number(String(r?.cost ?? "").trim() || 0),
      0,
    );
    return sum > 0;
  });
}

/**
 * True if the user entered any value in tier cost, stratification name/cost (vs baseline
 * names for strat), or implant name/cost. Use stratRowNameBaselineByTier when stratification
 * is on: one string[] per tier (initial/default names per row) so prefilled labels don't count.
 */
export function hasUserFilledAnyCostMappingInput(
  stratification,
  tiers,
  implantRows,
  stratRowNameBaselineByTier = null
) {
  if (Array.isArray(implantRows)) {
    for (const r of implantRows) {
      const n = String(r?.implantName ?? r?.name ?? "").trim();
      const c = String(r?.implantCost ?? r?.cost ?? "").trim();
      if (n !== "" || c !== "") return true;
    }
  }
  if (!Array.isArray(tiers)) return false;
  if (!stratification) {
    return tiers.some((t) => String(t?.cost ?? "").trim() !== "");
  }
  if (!stratRowNameBaselineByTier || stratRowNameBaselineByTier.length === 0) {
    for (const tier of tiers) {
      for (const r of tier.rows || []) {
        if (String(r?.cost ?? "").trim() !== "") return true;
      }
    }
    return false;
  }
  for (let ti = 0; ti < tiers.length; ti++) {
    const rows = tiers[ti]?.rows || [];
    const baseline = stratRowNameBaselineByTier[ti];
    for (let ri = 0; ri < rows.length; ri++) {
      const r = rows[ri];
      if (String(r?.cost ?? "").trim() !== "") return true;
      const nm = String(r?.name ?? "").trim();
      const base =
        baseline && baseline.length > ri ? String(baseline[ri] ?? "").trim() : null;
      if (base != null && nm !== base) return true;
      if (base == null && nm !== "") return true;
    }
  }
  return false;
}

/** @deprecated use hasUserFilledAnyCostMappingInput with baseline */
export function hasAnyProcedureCostMappingDetail(
  stratification,
  tiers,
  implantRows,
  stratRowNameBaselineByTier = null
) {
  return hasUserFilledAnyCostMappingInput(
    stratification,
    tiers,
    implantRows,
    stratRowNameBaselineByTier
  );
}
